import {Injectable} from '@angular/core';
import {Action, State, StateContext, Store} from '@ngxs/store';
import {zip} from 'rxjs';
import {take} from 'rxjs/operators';
import {CommonService} from 'src/app/services/common.service';
import {
  AccessPoint,
  AccessPointAlert,
  AccessPointAlertType,
  AccessPointDirection,
  AccessPointsQeuryResult,
  DoorConfigurationMetaData,
  Peripheral
} from '../../models/access-point/access-point';
import {AccessPointService} from '../../services/access-point.service';
import {updateStateItem, updateStateItemInList} from '../../utils/state-updates';
import {SharedState} from '../shared/shared.state';
import {
  GetDoorsConfiguration,
  LoadAccessPoints,
  LoadSelectedAccessPointData,
  LoadUserAccessPoints,
  OpenCloseAccessPoint,
  SearchAccessPoints,
  SearchAccessPointsAccessGroups,
  SearchAccessPointsOccupants,
  SecureAccessPoint,
  SetSelectedAccessPoint,
  UpdateAccessPointDoorPreviousTimeSeconds,
  UpdateAccessPointLocally,
  UpdateAccessPoints,
  UpdateSecureRequestStatus
} from './access-point.actions';
import {AccessPointStateModel} from './access-point.model';
import {TicksToDateService} from '../../services/ticks-to-date.service';
import {SecondsToMinutesPipe} from '../../../shared/pipes/seconds-to-minutes.pipe';

@State<AccessPointStateModel>({
  name: 'accessPoint',
  defaults: {
    accessPoints: null,
    lastUpdatedAccessPointId: null,
    selectedAccessPoint: null,
    accessPointListSearchTerm: '',
    currentAccessPointListPageNumber: 1,
    currentAccessPointOccupantListPageNumber: 0,
    occupantsListSearchTerm: '',
    accessGroupsListSearchTerm: '',
    currentAccessPointAccessGroupsListPageNumber: 0,
    currentPage: 0,
    totalDoorsCount: 0,
    isTouched: false,
    isLoadingData: false,
    accessPointPreviousTimeSeconds: [],
    configuration: null,
  }
})
@Injectable()
export class AccessPointState {

  constructor(
    private readonly accessPointService: AccessPointService,
    private store: Store,
    private commonService: CommonService,
    private ticksToDateService: TicksToDateService
  ) {
  }


  @Action(GetDoorsConfiguration)
  async getUsersConfiguration(ctx: StateContext<AccessPointStateModel>) {
    ctx.patchState({isLoadingData: true});
    const configuration: DoorConfigurationMetaData = await this.accessPointService.getConfiguration();
    if (configuration) {
      ctx.patchState({isLoadingData: false});
      ctx.patchState({configuration});
    } else {
      setTimeout(async () => {
        const configuration: DoorConfigurationMetaData = await this.accessPointService.getConfiguration();
        if (configuration) {
          ctx.patchState({isLoadingData: false});
          ctx.patchState({configuration});
        }
      }, 3000);
    }
  }

  @Action(LoadAccessPoints)
  async getAccessPoints(ctx: StateContext<AccessPointStateModel>, action: LoadAccessPoints) {
    if (action.resetChunk) {
      ctx.patchState({currentPage: 0});
    }
    let chunkSize: number = 1000;
    if (!action.loadAll) {
      chunkSize = this.store.selectSnapshot(SharedState.resultsChunkSize);
    }

    let {currentPage, accessPoints} = ctx.getState();
    let nextPage: number = currentPage;
    const accessPointResults = await this.accessPointService.getAccessPoints(++nextPage, chunkSize, ctx.getState().accessPointListSearchTerm);
    if (accessPointResults) {
      const accessPointsList = action.resetChunk ? [] : accessPoints;
      accessPointResults?.results?.forEach((accessPoint) => this.accessPointRutine(accessPoint, ctx.getState().accessPoints ? ctx.getState().accessPoints : []));
      if (accessPointResults?.results) {
        accessPointResults.results.sort(this.commonService.compareByDoorNameAscFn);
      }
      ctx.patchState({
        isTouched: true,
        accessPoints: [...accessPointsList, ...accessPointResults.results],
        totalDoorsCount: accessPointResults.totalCount
      });
      if (accessPointsList.length !== ctx.getState().accessPoints.length) {
        ctx.patchState({
          currentPage: nextPage
        });
      }
    }
  }

  @Action(LoadUserAccessPoints)
  async loadAccessPoints(
    ctx: StateContext<AccessPointStateModel>,
    action: LoadUserAccessPoints
  ) {
    let accessPointListPageNumber = ctx.getState().currentAccessPointListPageNumber;
    if (accessPointListPageNumber === 0) {
      accessPointListPageNumber = 1;
    }
    const chunkSize: number = this.store.selectSnapshot(SharedState.resultsChunkSize);
    const accessPoints = await this.accessPointService.getAccessPoints(accessPointListPageNumber, chunkSize, ctx.getState().accessPointListSearchTerm);
    if (accessPoints === null || accessPoints.results.length === 0) {
      return;
    }
    accessPoints.results.forEach((accessPoint) => this.accessPointRutine(accessPoint));

    ctx.patchState({
      accessPoints: [
        ...accessPoints.results
      ]
    });

  }

  @Action(SearchAccessPoints)
  searchAccessPoints(ctx: StateContext<AccessPointStateModel>, action: SearchAccessPoints) {
    let accessPointListPageNumber = ctx.getState().currentAccessPointListPageNumber;
    const chunkSize: number = this.store.selectSnapshot(SharedState.resultsChunkSize);
    if (accessPointListPageNumber === 0) {
      accessPointListPageNumber = 1;
    }
    ctx.patchState({
      accessPointListSearchTerm: action.searchTerm,
      currentAccessPointListPageNumber: accessPointListPageNumber
    });
    this.accessPointService
      .searchAccessPoints(
        1,
        chunkSize,
        ctx.getState().accessPointListSearchTerm
      )
      .then((result: AccessPointsQeuryResult) => {
        if (result === null || result.accessPoints === null || result.accessPoints.results.length === 0) {
          ctx.patchState({accessPoints: []});
        }
        result.accessPoints.results.forEach((accessPoint) => this.accessPointRutine(accessPoint));
        ctx.patchState({
          accessPoints: result.accessPoints.results,
          totalDoorsCount: result.accessPoints.totalCount
        });
      }).catch(() => {
      return null;
    });
  }

  @Action(SecureAccessPoint)
  secureAccessPoint(ctx: StateContext<AccessPointStateModel>, action: any) {
    return this.accessPointService.secureAccessPoint(
      action.accessPointId,
      !action.currentState,
      action.secureInSeconds
    );
  }

  @Action(UpdateSecureRequestStatus)
  updateSecureRequestStatus(
    ctx: StateContext<AccessPointStateModel>,
    action: UpdateSecureRequestStatus
  ) {
    ctx.patchState({lastUpdatedAccessPointId: null});
    updateStateItemInList(
      () => ctx.getState().accessPoints,
      (list: AccessPoint[]) =>
        this.getItemWithIndex(list, action.accessPointId),
      (item: AccessPoint) => {
        item.secureRequestStatus = action.status;
        item.isSecured = action.secureState;
      }
    );
    ctx.patchState({lastUpdatedAccessPointId: action.accessPointId});
  }

  @Action(OpenCloseAccessPoint)
  openAccessPoint(
    ctx: StateContext<AccessPointStateModel>,
    action: OpenCloseAccessPoint
  ) {
    // return this.accessPointService.openCloseAccessPoint(action.accessPointId, action.open).pipe(
    //   tap(() => {
    //     //ctx.patchState({accessPoints: [...ctx.getState().accessPoints]});
    //   })
    // );
    return this.accessPointService.openCloseAccessPoint(action.accessPointId, action.open, action.doorDefaultTimeSeconds);
  }

  @Action(SetSelectedAccessPoint)
  async selectAccessPoint(
    ctx: StateContext<AccessPointStateModel>,
    action: SetSelectedAccessPoint
  ) {
    ctx.patchState({isLoadingData: true});
    const accessPoint = ctx.getState().selectedAccessPoint;
    if (!action.accessPoint) {
      ctx.patchState({selectedAccessPoint: null, isLoadingData: false});
      return;
    }
    if (accessPoint) {
      ctx.dispatch(new LoadSelectedAccessPointData(action.accessPoint.id, true));
    }

    updateStateItem(
      () => action.accessPoint,
      async (item: AccessPoint) => {
        ctx.patchState({
          selectedAccessPoint: item,
          currentAccessPointOccupantListPageNumber: 0,
          occupantsListSearchTerm: '',
          currentAccessPointAccessGroupsListPageNumber: 0,
          accessGroupsListSearchTerm: ''
        });
      }
    );

    ctx.patchState({isLoadingData: false});
  }

  @Action(UpdateAccessPointLocally)
  async updateAccessPointLocally(ctx: StateContext<AccessPointStateModel>, action: UpdateAccessPointLocally) {

    if (ctx.getState().selectedAccessPoint?.id === action.accessPoint.id) {
      ctx.patchState({selectedAccessPoint: {...ctx.getState().selectedAccessPoint, ...action.accessPoint} });
    }
    let accessPoints = ctx.getState().accessPoints.map(item => {
      if (action.accessPoint.id === item.id) {
        return action.accessPoint;
      }
      return item;
    });

    ctx.patchState({accessPoints});

  }

  @Action(SearchAccessPointsOccupants)
  async searchAccessPointUsers(
    ctx: StateContext<AccessPointStateModel>,
    action: SearchAccessPointsOccupants
  ) {
    if (ctx.getState().selectedAccessPoint?.id) {
      ctx.patchState({isLoadingData: true});
      const {id} = ctx.getState().selectedAccessPoint;
      const chunkSize: number = this.store.selectSnapshot(SharedState.resultsChunkSize);
      const response = await this.accessPointService.searchAccessPointOccupants(
        id,
        action.page,
        chunkSize,
        action.searchTerm
      );

      if (response?.occupants) {
        action.occupantsSubject$.next(response.occupants.results);
      }
      ctx.patchState({isLoadingData: false});
    }
  }

  @Action(LoadSelectedAccessPointData)
  async LoadSelectedAccessPointData(ctx: StateContext<AccessPointStateModel>, action: LoadSelectedAccessPointData) {
    ctx.patchState({isLoadingData: true});
    const chunkSize: number = this.store.selectSnapshot(SharedState.resultsChunkSize);
    zip(this.accessPointService.searchAccessPointAccessGroups(
        action.accessPointId,
        1,
        chunkSize,
        ''
      ),
      this.accessPointService.searchAccessPointOccupants(
        action.accessPointId,
        1,
        chunkSize,
        ''
      )).pipe(take(1)).subscribe(data => { // unsubscribe after first emmit
      if (data) {
        const accessGroupsResults = data[0]?.accessGroups.results;
        const occupantsResults = data[1]?.occupants.results;
        if (accessGroupsResults && occupantsResults && ctx.getState().selectedAccessPoint?.id) {
          ctx.patchState({
            selectedAccessPoint: {
              ...ctx.getState().selectedAccessPoint,
              users: [...occupantsResults],
              accessGroups: [...accessGroupsResults]
            }
          });
        }
      }
      ctx.patchState({isLoadingData: false});
    });
  }

  @Action(SearchAccessPointsAccessGroups)
  async searchAccessPointAccessGroups(
    ctx: StateContext<AccessPointStateModel>,
    action: SearchAccessPointsAccessGroups
  ) {
    if (ctx.getState().selectedAccessPoint?.id) {
      ctx.patchState({isLoadingData: true});
      const {id} = ctx.getState().selectedAccessPoint;
      const chunkSize: number = this.store.selectSnapshot(SharedState.resultsChunkSize);
      const response = await this.accessPointService.searchAccessPointAccessGroups(
        id,
        action.page,
        chunkSize,
        action.searchTerm
      );
      if (response?.accessGroups && action.accessGroupSubject$) {
        action.accessGroupSubject$.next(response.accessGroups.results);
      }
      ctx.patchState({isLoadingData: false});
    }
  }

  @Action(UpdateAccessPoints)
  updateAccessPoints(ctx: StateContext<AccessPointStateModel>, action: UpdateAccessPoints) {
    if (action.payload == null) {
      return;
    }
    if (ctx.getState().accessPoints) {
      let pointsState = ctx.getState().accessPoints;
      let updatedPoint = action.payload.accessPoints[0];
      const index = pointsState.findIndex(current => current.id === updatedPoint.id);
      if (index !== -1) {
        ctx.getState().accessPoints[index].isOpen = updatedPoint.isOpen;
        ctx.getState().accessPoints[index].isSecured = updatedPoint.isSecured;
        ctx.getState().accessPoints[index].remainingTime = updatedPoint.remainingTime;
        ctx.getState().accessPoints[index].isLockdown = updatedPoint.isLockdown;
        const {selectedAccessPoint} = ctx.getState();
        if (selectedAccessPoint?.id === updatedPoint.id) {
          ctx.patchState({
            selectedAccessPoint: {
              ...selectedAccessPoint,
              isOpen: updatedPoint.isOpen,
              isSecured: updatedPoint.isSecured,
              isLockdown: updatedPoint.isLockdown
            }
          });
        }
      }

      ctx.patchState({accessPoints: [...ctx.getState().accessPoints]});
    }
  }

  @Action(UpdateAccessPointDoorPreviousTimeSeconds)
  UpdateAccessPointDoorPreviousTimeSeconds(ctx: StateContext<AccessPointStateModel>, action: UpdateAccessPointDoorPreviousTimeSeconds) {
    if (action.payload == null) {
      return;
    }
    if (ctx.getState().accessPoints) {
      let pointsPreviousTimeState = ctx.getState().accessPointPreviousTimeSeconds;
      let doorPrevTime = action.payload;
      const index = pointsPreviousTimeState.findIndex(current => current.id === doorPrevTime.id);
      if (index !== -1) {
        ctx.getState().accessPointPreviousTimeSeconds[index].doorPreviousTimeSeconds = doorPrevTime.doorPreviousTimeSeconds;
      } else {
        ctx.getState().accessPointPreviousTimeSeconds.push(doorPrevTime);
      }

      ctx.patchState({accessPointPreviousTimeSeconds: [...ctx.getState().accessPointPreviousTimeSeconds]});
    }
  }

  private getItemWithIndex(
    list: AccessPoint[],
    accessPointId: string
  ): { index: number; item: AccessPoint } {
    const currentAccessPointIndex = list.findIndex(
      (accessPoint) => accessPoint.id === accessPointId
    );
    return {
      index: currentAccessPointIndex,
      item: list[currentAccessPointIndex]
    };
  }

  private setAccessPointDirection(
    peripherals: Peripheral
  ): AccessPointDirection {
    if (
      peripherals.readers.find(
        (item) => item.direction === AccessPointDirection.Entry
      ) &&
      peripherals.readers.find(
        (item) => item.direction === AccessPointDirection.Exit
      )
    ) {
      return AccessPointDirection.TwoWay;
    } else if (
      peripherals.readers.find(
        (item) => item.direction === AccessPointDirection.Entry
      ) &&
      !peripherals.readers.find(
        (item) => item.direction === AccessPointDirection.Exit
      )
    ) {
      return AccessPointDirection.Entry;
    } else {
      return AccessPointDirection.Exit;
    }
  }

  private setAlertsTimeStrings(alerts: AccessPointAlert[]): AccessPointAlert[] {
    if (alerts === []) {
      return [];
    }
    alerts.forEach((value) => {
      if (value.type === AccessPointAlertType.ForceOpen) {
        value.timeString = `at: ${this.ticksToDateService.ticksToDate(value.time).toLocaleTimeString()}`;
      } else {
        const date1 = new Date(Date.now());
        const date2 = this.ticksToDateService.ticksToDate(value.time);
        const diffTime = date2.getTime() - date1.getTime();
        const seconds = Math.ceil(diffTime / 1000);

        const stm = new SecondsToMinutesPipe();
        value.timeString = `for: ${stm.transform(seconds, false, true)}`;
      }
    });
    return alerts;
  }

  private accessPointRutine(accessPoint: AccessPoint, localAccessPoints: AccessPoint[] = []) {
    accessPoint.alerts = this.setAlertsTimeStrings(accessPoint.alerts);
    accessPoint.direction = this.setAccessPointDirection(accessPoint.peripherals);
    accessPoint.openRequestStatus = this.setAccessPointOpenRequestStatus(accessPoint, localAccessPoints);
  }

  private setAccessPointOpenRequestStatus(accessPoint: AccessPoint, localAccessPoints: AccessPoint[] = []) {
    if (localAccessPoints.length > 0) {
      const status = localAccessPoints.find(item => item.id === accessPoint.id)?.openRequestStatus;
      return status ? status : 0;
    }
  }
}
